/**
 * Plaintiff-facing FAQ content.
 *
 * Pure data — no React and no icon imports, so this module stays unit-testable
 * and can also generate the FAQPage JSON-LD from the same array the page renders.
 * Category icons live in `pages/FAQ.js`.
 *
 * Copy rules enforced by faqContent.test.js:
 *   - no percentage, rate, cap multiple, or platform fee figure anywhere
 *   - dollar amounts only in `how-much-can-i-request`
 *   - "loan" only in `is-this-a-loan`, where it is refuted
 * See docs/superpowers/specs/2026-07-29-faq-page-design.md.
 */

export const FAQ_CATEGORIES = [
  {
    id: 'basics',
    label: 'The Basics',
    questions: [
      {
        id: 'is-this-a-loan',
        q: 'Is this a loan?',
        body: [
          'No. Despite our name, what you receive through MyCaseLoan is not a loan — it is a non-recourse advance on the settlement you may receive from your pending case.',
          'A loan has to be repaid no matter what happens. A non-recourse advance is repaid only out of your settlement. If your case does not resolve in your favor, you keep the funds and owe nothing.',
          'There are no monthly payments and no credit consequences. Repayment happens once, at the end of your case, handled by your attorney out of the proceeds.',
        ],
      },
      {
        id: 'what-is-pre-settlement-funding',
        q: 'What is pre-settlement funding?',
        body: [
          'Personal injury cases can take a year or longer to resolve, but bills do not pause while you wait. Pre-settlement funding lets you access part of your expected settlement now instead of waiting for your case to conclude.',
          'A funder advances you money today. In exchange, they are repaid an agreed amount out of your settlement when your case resolves. If there is no recovery, there is nothing for them to collect.',
        ],
      },
      {
        id: 'how-does-mycaseloan-work',
        q: 'How does MyCaseLoan work?',
        body: [
          'MyCaseLoan is a marketplace, not a funding company. There are three steps.',
          'First, you apply. A short online application captures your case type, your attorney, and how much funding you need.',
          'Second, your attorney verifies. We notify your attorney, who confirms they represent you and adds the case details funders need in order to evaluate your claim.',
          'Third, funders compete. Qualified funders review your verified case and submit offers. You compare the terms side by side and choose the one that works best for you — or none at all.',
        ],
        link: { label: 'See how it works', to: '/#how-it-works' },
      },
      {
        id: 'does-mycaseloan-fund-me-directly',
        q: 'Does MyCaseLoan give me the money directly?',
        body: [
          'No. MyCaseLoan is not a lender and not a funding company. We operate the platform that connects you, your attorney, and the funders who make offers.',
          'The money comes from the funder whose offer you accept, and the terms of your advance are between you and that funder.',
        ],
        link: { label: 'Read our Terms of Service', to: '/terms' },
      },
      {
        id: 'what-can-i-use-the-funds-for',
        q: 'What can I use the funds for?',
        body: [
          'Anything you need to get through this period while your case is active. There are no restrictions on how you spend your advance.',
          'Most people use it for the ordinary costs that do not stop after an injury:',
        ],
        bullets: [
          'Rent or mortgage payments',
          'Utilities and groceries',
          'Car payments and transportation to medical appointments',
          'Childcare and family expenses',
          'Out-of-pocket medical costs',
        ],
      },
    ],
  },
  {
    id: 'cost',
    label: 'Cost & Repayment',
    questions: [
      {
        id: 'what-does-an-advance-cost',
        q: 'What does an advance cost?',
        body: [
          'Every offer you receive spells out three things, and together they determine what you will owe.',
          'The fees, which are added to your balance at the start rather than charged to you upfront. The rate, which is how quickly your balance grows while your case is open. And the cap, if the offer includes one, which is the most you could ever owe.',
          'Each funder sets its own numbers, which is exactly why receiving more than one offer matters — you can compare all three side by side. Every offer also includes a schedule showing what you would owe if your case settled on specific dates, so you can see the real cost before you sign anything.',
        ],
      },
      {
        id: 'why-is-my-balance-more-than-my-cash',
        q: 'Why is my starting balance higher than the cash I receive?',
        body: [
          'Because the fees are financed into your advance instead of being charged to you upfront. The cash you receive plus those fees equals your starting balance, and your starting balance is the amount the rate applies to.',
          'This is the trade-off for paying nothing out of pocket while your case is pending. You will see both numbers — the cash to you and the starting balance — clearly separated in every offer before you accept it.',
        ],
      },
      {
        id: 'how-is-the-cost-calculated',
        q: 'How is the cost calculated?',
        body: [
          'Your starting balance grows at an annual rate set by the funder, applied in six-month steps.',
          'The growth is simple, not compounding. Each step adds the same amount, calculated on your starting balance — never on charges that have already accrued. This matters: it means your balance grows in a straight line rather than accelerating over time.',
          'Your offer lists exactly what you would owe at each six-month point, so nothing about the cost is left to interpretation.',
        ],
      },
      {
        id: 'will-i-owe-more-over-time',
        q: 'Will I owe more the longer my case takes?',
        body: [
          'Yes, up to the cap if your offer has one. Your balance grows at each six-month step, so a case that settles in under a year costs less than the same case settling three years later.',
          'Two things limit how far it can go. The growth is simple rather than compounding, and many offers include a cap beyond which your balance stops growing entirely.',
          'When you are comparing offers, look at the settle-by schedule against a realistic timeline for your own case. Ask your attorney how long they expect your case to take, then compare what each offer would cost at that point rather than at the earliest date shown.',
        ],
      },
      {
        id: 'is-there-a-limit-on-what-i-owe',
        q: 'Is there a limit on what I could owe?',
        body: [
          'Many offers include a cap — a maximum total repayment that your balance cannot grow past, no matter how long your case runs.',
          'Caps are set by the funder, so they vary from offer to offer, and not every offer will include one. When an offer has a cap, it is shown alongside the repayment schedule.',
          'This is one of the most important things to compare. An offer with a lower rate but no cap can end up costing more than one with a higher rate and a firm cap, if your case runs long.',
        ],
      },
      {
        id: 'do-i-pay-anything-upfront',
        q: 'Do I pay anything upfront?',
        body: [
          'No. There is no application fee, no credit check fee, and nothing due at any point while your case is pending.',
          'Applying on MyCaseLoan is free. The fees are financed into your balance, and everything is settled out of your case proceeds when it resolves.',
        ],
      },
      {
        id: 'what-happens-if-i-lose',
        q: 'What happens if I lose my case?',
        body: [
          'You keep the money, and you owe nothing.',
          'This is what makes an advance non-recourse, and it is the single biggest difference between this and borrowing. The funder takes on the risk that your case may not succeed. If there is no recovery, there is nothing for them to be repaid out of, and they cannot come after you, your wages, your home, or your other assets.',
        ],
      },
      {
        id: 'how-is-the-advance-repaid',
        q: 'How is the advance repaid?',
        body: [
          'You never write a check. When your case resolves, your attorney pays the amount owed as of that date directly to the funder out of the settlement proceeds, then releases the remainder to you.',
          'There are no monthly payments and nothing for you to remember. Repayment happens once, automatically, at the end of your case.',
        ],
      },
      {
        id: 'how-long-is-the-repayment-term',
        q: 'How long do I have to repay?',
        body: [
          'There is no due date and no fixed term. Repayment happens whenever your case resolves, however long that takes.',
          'What varies between offers is the schedule — how the balance grows over time. Offers commonly lay this out from twelve months through thirty-six months or beyond, which is why comparing those schedules against your attorney’s expected timeline is worth the few minutes it takes.',
        ],
      },
    ],
  },
  {
    id: 'eligibility',
    label: 'Eligibility',
    questions: [
      {
        id: 'do-you-check-my-credit',
        q: 'Do you check my credit?',
        body: [
          'No. There is no credit check anywhere in the process, and applying does not affect your credit score.',
          'Your credit history is not relevant here, because repayment does not depend on your finances. It depends on your case.',
        ],
      },
      {
        id: 'do-i-need-a-job',
        q: 'Do I need a job or income to qualify?',
        body: [
          'No. There is no employment verification and no income requirement. Many people seeking pre-settlement funding are out of work precisely because of the injury involved in their case.',
          'What funders evaluate is the strength of your claim, not your paycheck.',
        ],
      },
      {
        id: 'what-types-of-cases-qualify',
        q: 'What types of cases qualify?',
        body: ['MyCaseLoan currently accepts applications for:'],
        bullets: [
          'Auto accidents and motor vehicle accidents',
          'Ride share auto accidents',
          'Slip and fall, or injury on someone else’s property',
          'Dog bites',
          'Medical malpractice',
          'Other personal injury matters',
        ],
        links: [
          { label: 'Auto accident funding', to: '/auto-accident-funding' },
          { label: 'Ride share accident funding', to: '/rideshare-accident-funding' },
          { label: 'Slip and fall funding', to: '/slip-and-fall-funding' },
          { label: 'Dog bite funding', to: '/dog-bite-funding' },
          { label: 'Medical malpractice funding', to: '/medical-malpractice-funding' },
        ],
      },
      {
        id: 'do-i-need-an-attorney',
        q: 'Do I need an attorney to apply?',
        body: [
          'Yes. You need to be represented by an attorney, because your attorney has to verify your case before any funder can evaluate it.',
          'This protects you. Attorney verification means no funder ever sees an unverified claim, and it means you have your own advocate available to review any offer before you accept it.',
        ],
      },
      {
        id: 'how-much-can-i-request',
        q: 'How much can I request?',
        body: [
          'The application lets you request anywhere from under $5,000 to over $100,000.',
          'What you are actually offered depends on your case. The funders reviewing it consider the details your attorney verifies, and they may offer less than you requested — or make no offer at all.',
        ],
      },
    ],
  },
  {
    id: 'timeline',
    label: 'Timeline & Process',
    questions: [
      {
        id: 'how-long-does-the-application-take',
        q: 'How long does the application take?',
        body: [
          'A few minutes. It is four short steps: your case type and contact details, your attorney’s information, how much funding you need, and a brief description of what happened.',
          'Some case documents may be needed. For auto and ride share accidents a police report is required, and insurance information may be requested as well. You do not need them in hand to start — you can submit your application first and add documents afterwards, though providing them sooner helps funders review your case faster.',
          'You will not need financial records, pay stubs, or credit history to apply. If your attorney is not on the platform yet, you can still finish your application.',
        ],
        link: { label: 'Start your application', to: '/apply' },
      },
      {
        id: 'how-soon-could-i-receive-funds',
        q: 'How soon could I receive funds?',
        body: [
          'There is no single answer, because the timeline depends on two things that happen after you apply: how quickly your attorney completes verification, and how quickly funders review your case and respond with offers.',
          'What we can promise is that you will not be left guessing. Your dashboard shows exactly where your application stands at every stage — submitted, verified by your attorney, under funder review, and offers received.',
          'The most useful thing you can do to keep it moving is make sure your attorney’s contact information on your application is correct, since verification is the step that unlocks everything after it.',
        ],
      },
      {
        id: 'will-i-receive-more-than-one-offer',
        q: 'Will I receive more than one offer?',
        body: [
          'Often, yes. Once your attorney verifies your case, it becomes visible to qualified funders on the platform, and more than one may submit an offer.',
          'This is the main reason to apply through a marketplace rather than contacting a single funding company. You can compare the cash amount, the fees, the rate, and the cap side by side, and pick the offer that actually suits your situation.',
          'Multiple offers are common but not guaranteed — some cases attract several, others attract one or none. You are never obligated to accept any offer you receive.',
        ],
      },
      {
        id: 'how-do-i-check-my-status',
        q: 'How do I check my application status?',
        body: [
          'Log in to your MyCaseLoan account. Your dashboard shows exactly where your application stands, any documents that have been requested, and any offers you have received.',
          'You will also be notified by email when something changes — when your attorney verifies your case, and when an offer arrives.',
        ],
        link: { label: 'Log in to your account', to: '/login' },
      },
    ],
  },
  {
    id: 'attorney-privacy',
    label: 'Your Attorney & Your Privacy',
    questions: [
      {
        id: 'what-does-my-attorney-have-to-do',
        q: 'What does my attorney have to do?',
        body: [
          'Less than you might expect. Your attorney confirms that they represent you, adds the case details funders need, and uploads any supporting documents that get requested.',
          'They do not take on any repayment obligation, and they are not asked to guarantee anything about the outcome of your case. When your case settles, they pay the funder out of your proceeds as part of the normal disbursement process.',
        ],
        link: { label: 'What we offer law firms', to: '/for-law-firms' },
      },
      {
        id: 'what-if-my-attorney-isnt-on-the-platform',
        q: 'What if my attorney is not on MyCaseLoan yet?',
        body: [
          'That is completely normal — most applications start that way. Enter your attorney’s or firm’s information in the application and we will invite them to verify your case.',
          'There is no cost to your attorney for using the platform, and they can complete verification from a secure link without setting anything up in advance.',
        ],
      },
      {
        id: 'who-can-see-my-case-information',
        q: 'Who can see my case information?',
        body: [
          'Only the people who need to. MyCaseLoan uses role-based access, so each party sees a defined slice of your case: you see everything on your own application, your attorney sees the case they are verifying, and funders see the information needed to make a funding decision.',
          'Your case is not published, listed publicly, or sold as a lead. Funders only see it after your attorney has verified it.',
        ],
      },
      {
        id: 'is-my-information-secure',
        q: 'Is my personal information secure?',
        body: [
          'Yes. Your data is encrypted in transit and at rest, access is controlled by role, and every action taken on your application — verifications, document uploads, offers — is recorded with a timestamp.',
          'Documents you upload go into secure, access-controlled storage rather than being emailed around.',
        ],
        link: { label: 'Read our Privacy Policy', to: '/privacy' },
      },
    ],
  },
];

/**
 * Build schema.org FAQPage structured data from the same array the page renders,
 * so the visible copy and the structured data can never drift apart.
 * Body paragraphs and bullets are joined; `link` is intentionally excluded.
 */
export function buildFaqJsonLd(categories = FAQ_CATEGORIES) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: categories.flatMap((category) =>
      category.questions.map((question) => ({
        '@type': 'Question',
        name: question.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: [...question.body, ...(question.bullets || [])].join(' '),
        },
      })),
    ),
  };
}

/** Resolve a question id (from a URL hash) to its category. */
export function findQuestionLocation(questionId) {
  for (const category of FAQ_CATEGORIES) {
    const match = category.questions.find((question) => question.id === questionId);
    if (match) {
      return { categoryId: category.id, questionId: match.id };
    }
  }
  return null;
}
